'use client'

import { ReactNode, useState } from 'react'
//import { Helmet } from "react-helmet-async";
import Link from 'next/link'
import { ArrowRight, ArrowDown, Plug } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SOLUTIONS, SolutionMeta, getSolution } from '@/data/solutions'
import { CASES } from '@/data/cases'
import SolutionLeadDialog from '@/components/platform/v2/SolutionLeadDialog'
import { PageCta, PageHero } from '@/components/platform/v2/PageEdge'

/* ------------------------------------------------------------------ shell */

export const SolutionPage = ({ meta, children }: { meta: SolutionMeta; children: ReactNode }) => (
    <>
        {/*<div className="min-h-screen">
            <Seo title={meta.title} description={meta.description} path={meta.path} />
            <Helmet>
            <script type="application/ld+json">
                {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Главная", item: `${ORIGIN}/` },
                    { "@type": "ListItem", position: 2, name: "Решения", item: `${ORIGIN}/solutions` },
                    { "@type": "ListItem", position: 3, name: meta.nav, item: `${ORIGIN}${meta.path}` },
                ],
                })}
            </script>
            </Helmet>
            <Header />*/}
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            <nav aria-label="Хлебные крошки" className="container-page pt-4">
                <ol className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <li>
                        <Link href="/" className="hover:text-primary">
                            Главная
                        </Link>
                    </li>
                    <li aria-hidden>/</li>
                    <li>
                        <Link href="/platform/solutions" className="hover:text-primary">
                            Решения
                        </Link>
                    </li>
                    <li aria-hidden>/</li>
                    <li className="text-foreground/80">{meta.nav}</li>
                </ol>
            </nav>
            {children}
        </div>
    </>
)

export const Block = ({
    id,
    children,
    tone = 'dark',
    className,
}: {
    id?: string
    children: ReactNode
    tone?: 'dark' | 'quiet' | 'light'
    className?: string
}) => (
    <section
        id={id}
        className={cn(
            'section-y',
            tone === 'quiet' && 'bg-[hsl(var(--deep-black))]/60',
            tone === 'light' && 'bg-[hsl(var(--warm-light))] text-[hsl(var(--deep-black))]',
            className,
        )}
    >
        <div className="container-page">{children}</div>
    </section>
)

export const Head = ({
    eyebrow,
    title,
    text,
    align = 'left',
    light,
}: {
    eyebrow?: string
    title: ReactNode
    text?: ReactNode
    align?: 'left' | 'center'
    light?: boolean
}) => (
    <div className={cn('flex flex-col gap-4', align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl')}>
        {eyebrow && <p className={cn('eyebrow', light && 'text-[hsl(var(--deep-black))]/60')}>{eyebrow}</p>}
        <h2 className={cn('display-2', light && 'text-[hsl(var(--deep-black))]')}>{title}</h2>
        {text && <p className={cn('lede', light && 'text-[hsl(var(--deep-black))]/70')}>{text}</p>}
    </div>
)

/* -------------------------------------------------------------------- hero */

export const SolutionHero = ({
    meta,
    eyebrow,
    titleLines,
    lead,
    primaryLabel,
    primaryTo,
    secondary,
    visual,
    audience,
}: {
    meta: SolutionMeta
    eyebrow: string
    titleLines: string[]
    lead: string
    primaryLabel: string
    primaryTo?: string
    secondary?: { to: string; label: string }
    visual?: ReactNode
    audience?: string[]
}) => {
    const [open, setOpen] = useState(false)
    return (
        <>
            <PageHero
                eyebrow={eyebrow}
                title={
                    <>
                        {titleLines.map((l, i) => (
                            <span key={l} className={cn('block', i === titleLines.length - 1 && 'text-primary')}>
                                {l}
                            </span>
                        ))}
                    </>
                }
                text={lead}
                actions={
                    <>
                        {primaryTo ? (
                            <Link href={primaryTo} className="btn-primary w-full sm:w-auto">
                                {primaryLabel}
                            </Link>
                        ) : (
                            <button onClick={() => setOpen(true)} className="btn-primary w-full sm:w-auto">
                                {primaryLabel}
                            </button>
                        )}
                        {secondary && (
                            <Link href={secondary.to} className="btn-ghost w-full sm:w-auto">
                                {secondary.label} <ArrowRight className="h-4 w-4" />
                            </Link>
                        )}
                    </>
                }
                note={
                    audience && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            {audience.map((a) => (
                                <span key={a} className="pill pill-neutral">
                                    {a}
                                </span>
                            ))}
                        </div>
                    )
                }
                visual={visual}
            />
            {!primaryTo && <SolutionLeadDialog open={open} onOpenChange={setOpen} title={primaryLabel} scenario={meta.nav} />}
        </>
    )
}

/* --------------------------------------------------------------- recognize */

export const Recognize = ({
    eyebrow = 'Узнаёте ситуацию?',
    title,
    items,
    conclusion,
}: {
    eyebrow?: string
    title: string
    items: string[]
    conclusion: string
}) => (
    <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
        <Head eyebrow={eyebrow} title={title} />
        <div className="flex flex-col gap-3">
            {items.map((i, idx) => (
                <p
                    key={i}
                    className={cn(
                        'rounded-2xl border border-border/60 bg-[hsl(var(--charcoal))] px-5 py-4 text-sm text-foreground/85 sm:text-base',
                        idx % 2 === 1 && 'lg:ml-8',
                    )}
                >
                    {i}
                </p>
            ))}
            <p className="mt-3 text-base font-bold text-primary sm:text-lg">{conclusion}</p>
        </div>
    </div>
)

/* ------------------------------------------------------------- dual flows */

export const DualFlow = ({
    clientTitle = 'Сторона клиента',
    businessTitle = 'Сторона бизнеса',
    client,
    business,
}: {
    clientTitle?: string
    businessTitle?: string
    client: string[]
    business: string[]
}) => (
    <div className="grid gap-4 md:grid-cols-2">
        {[
            { title: clientTitle, steps: client, accent: true },
            { title: businessTitle, steps: business, accent: false },
        ].map((col) => (
            <div
                key={col.title}
                className={cn(
                    'rounded-[1.5rem] border p-6',
                    col.accent ? 'border-primary/35 bg-primary/[0.06]' : 'border-border/60 bg-[hsl(var(--charcoal))]',
                )}
            >
                <p className={cn('eyebrow', col.accent && 'text-primary')}>{col.title}</p>
                <ol className="mt-5 flex flex-col gap-2">
                    {col.steps.map((s, i) => (
                        <li key={s} className="flex flex-col gap-2">
                            <span
                                className={cn(
                                    'rounded-xl border px-4 py-3 text-sm font-semibold',
                                    col.accent
                                        ? 'border-primary/25 bg-[hsl(var(--background))]/40 text-foreground'
                                        : 'border-border/50 bg-[hsl(var(--deep-black))] text-foreground/90',
                                )}
                            >
                                {s}
                            </span>
                            {i < col.steps.length - 1 && <ArrowDown className="mx-auto h-4 w-4 text-primary/60" />}
                        </li>
                    ))}
                </ol>
            </div>
        ))}
    </div>
)

export const Chain = ({ steps, accentIndex }: { steps: string[]; accentIndex?: number }) => (
    <div className="flex flex-col gap-3 md:flex-row md:items-stretch">
        {steps.map((s, i) => (
            <div key={s} className="flex min-w-0 flex-1 flex-col gap-3 md:flex-row md:items-center">
                <div
                    className={cn(
                        'flex-1 rounded-2xl border px-4 py-4 text-sm font-bold',
                        i === accentIndex
                            ? 'border-primary/50 bg-primary/10 text-primary'
                            : 'border-border/60 bg-[hsl(var(--charcoal))] text-foreground',
                    )}
                >
                    {s}
                </div>
                {i < steps.length - 1 && (
                    <>
                        <ArrowDown className="mx-auto h-4 w-4 shrink-0 text-primary/70 md:hidden" />
                        <ArrowRight className="hidden h-4 w-4 shrink-0 text-primary/70 md:block" />
                    </>
                )}
            </div>
        ))}
    </div>
)

export const VerticalFlow = ({ steps }: { steps: string[] }) => (
    <ol className="mx-auto flex max-w-3xl flex-col items-center gap-2">
        {steps.map((step, index) => (
            <li key={step} className="flex w-full flex-col items-center gap-2">
                <div className="w-full rounded-2xl border border-border/60 bg-[hsl(var(--charcoal))] px-5 py-4 text-center text-sm font-bold text-foreground sm:text-base">
                    {step}
                </div>
                {index < steps.length - 1 && <ArrowDown className="h-4 w-4 text-primary/70" />}
            </li>
        ))}
    </ol>
)

export const FeatureGrid = ({ items, columns = 3 }: { items: { title: string; text?: string }[]; columns?: 2 | 3 }) => (
    <div className={cn('grid gap-3 sm:grid-cols-2', columns === 3 && 'lg:grid-cols-3')}>
        {items.map((item) => (
            <div
                key={item.title}
                className="flex min-h-32 flex-col justify-center rounded-2xl border border-border/60 bg-[hsl(var(--charcoal))] p-5 text-center"
            >
                <h3 className="text-base font-bold text-foreground">{item.title}</h3>
                {item.text && <p className="mt-2 text-sm text-muted-foreground">{item.text}</p>}
            </div>
        ))}
    </div>
)

/* ------------------------------------------------------- solution content */

export const SolutionKitGrid = ({ meta, title = 'Что входит в решение', text }: { meta: SolutionMeta; title?: string; text?: string }) => (
    <div className="flex flex-col gap-8">
        <Head eyebrow="Состав решения" title={title} text={text} />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {meta.kit.map((k) => (
                <Link
                    key={k.label + k.to}
                    href={k.to}
                    className="group flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-[hsl(var(--charcoal))] px-5 py-4 transition-all hover:border-primary/50"
                >
                    <span className="text-sm font-bold text-foreground">{k.label}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
                </Link>
            ))}
        </div>
    </div>
)

export const ExistingStack = ({ scenario }: { scenario: string }) => {
    return (
        <div className="surface flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:p-10">
            <div className="max-w-2xl space-y-3">
                <div className="flex items-center gap-2">
                    <Plug className="h-5 w-5 text-primary" />
                    <span className="eyebrow">Что уже есть у вас</span>
                </div>
                <h3 className="display-3">Уже есть сайт, CRM или 1С?</h3>
                <p className="body-text">
                    Не обязательно менять всё. VCENTRE.ONE может встроиться в существующий контур и стать клиентским и операционным слоем
                    поверх работающих систем.
                </p>
            </div>
            <Link href={`/signin?scenario=${scenario}&focus=existing-stack`} className="btn-primary w-full lg:w-auto">
                Показать, что у нас уже есть
            </Link>
        </div>
    )
}

export const SolutionCase = ({
    slugs,
    eyebrow = 'Релевантный кейс',
    title = 'Как это собрано у реального клиента',
    text,
}: {
    slugs: string[]
    eyebrow?: string
    title?: string
    text?: string
}) => {
    const items = slugs.map((s) => CASES.find((c) => c.slug === s)).filter(Boolean)
    if (!items.length) return null
    return (
        <div className="flex flex-col gap-8">
            <Head eyebrow={eyebrow} title={title} text={text} />
            <div className="grid gap-4 md:grid-cols-2">
                {items.map((c) => (
                    <Link
                        key={c!.slug}
                        href="/platform/cases"
                        className="group flex flex-col gap-3 rounded-2xl border border-border/60 bg-[hsl(var(--deep-black))] p-6 transition-all hover:border-primary/50"
                    >
                        <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/12 text-xs font-extrabold text-primary">
                                {c!.marker}
                            </span>
                            <div className="min-w-0">
                                <p className="truncate text-base font-bold text-foreground">{c!.client}</p>
                                <p className="truncate text-xs text-muted-foreground">{c!.category}</p>
                            </div>
                        </div>
                        <p className="text-sm text-muted-foreground">{c!.shortDescription}</p>
                        <span className="btn-link mt-auto">
                            Смотреть кейс <ArrowRight className="h-4 w-4" />
                        </span>
                    </Link>
                ))}
            </div>
        </div>
    )
}

export const OtherSolutions = ({ meta }: { meta: SolutionMeta }) => (
    <div className="flex flex-col gap-6">
        <Head eyebrow="Другие сценарии" title="Похоже, но не совсем ваш случай?" />
        <div className="grid gap-3 sm:grid-cols-3">
            {meta.related.map((id) => {
                const s = getSolution(id)
                return (
                    <Link
                        key={s.id}
                        href={s.path}
                        className="group flex flex-col gap-2 rounded-2xl border border-border/60 bg-[hsl(var(--charcoal))] p-5 transition-all hover:border-primary/50"
                    >
                        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary/80">{s.marker}</span>
                        <span className="text-base font-bold text-foreground">{s.nav}</span>
                        <span className="text-sm text-muted-foreground">{s.hint}</span>
                        <ArrowRight className="mt-2 h-4 w-4 text-primary transition-transform group-hover:translate-x-1" />
                    </Link>
                )
            })}
        </div>
    </div>
)

export const SolutionCta = ({
    title,
    text,
    buttonLabel,
    scenario,
    to,
    visual,
}: {
    title: ReactNode
    text?: string
    buttonLabel: string
    scenario: string
    to?: string
    visual?: ReactNode
}) => {
    const [open, setOpen] = useState(false)
    return (
        <>
            <PageCta
                title={title}
                text={text}
                visual={visual}
                actions={
                    <>
                        {to ? (
                            <Link href={to} className="btn-primary w-full sm:w-auto">
                                {buttonLabel}
                            </Link>
                        ) : (
                            <button onClick={() => setOpen(true)} className="btn-primary w-full sm:w-auto">
                                {buttonLabel}
                            </button>
                        )}
                        <Link href="/platform/pricing" className="btn-ghost w-full sm:w-auto">
                            Посмотреть тарифы <ArrowRight className="h-4 w-4" />
                        </Link>
                    </>
                }
            />
            {!to && <SolutionLeadDialog open={open} onOpenChange={setOpen} title={buttonLabel} scenario={scenario} />}
        </>
    )
}

export const FinalNote = () => (
    <p className="mx-auto max-w-2xl text-center text-sm text-muted-foreground">
        Не нашли точное совпадение? Это нормально. VCENTRE.ONE строится вокруг процесса, а не вокруг названия отрасли.
    </p>
)

export { SOLUTIONS }
